/**
 * Shared module icon catalog and resolver. Used by:
 *   - admin (IconSelect dropdown + module list rendering)
 *   - launcher (ServiceCard icon rendering)
 *
 * The single source of truth for icon keys → iconify identifiers, plus the
 * normalization aliases that map legacy values (snake_case, plurals, common
 * synonyms) to canonical keys.
 */

export interface ModuleIconOption {
  key: string;
  label: string;
  iconify: string;
}

export const MODULE_ICON_OPTIONS: ModuleIconOption[] = [
  { key: 'ticket', label: 'Ticket', iconify: 'solar:ticket-bold' },
  { key: 'gift', label: 'Gift', iconify: 'solar:gift-bold' },
  { key: 'membership', label: 'Membership', iconify: 'solar:card-bold' },
  { key: 'donation', label: 'Donation', iconify: 'solar:heart-bold' },
  { key: 'calendar', label: 'Calendar / Event', iconify: 'solar:calendar-bold' },
  { key: 'shop', label: 'Shop', iconify: 'solar:shop-bold' },
  { key: 'map', label: 'Map', iconify: 'solar:map-bold' },
  { key: 'info', label: 'Information', iconify: 'solar:info-circle-bold' },
  { key: 'user', label: 'Account', iconify: 'solar:user-bold' },
  { key: 'home', label: 'Home', iconify: 'solar:home-bold' },
  { key: 'museum', label: 'Museum', iconify: 'solar:museum-bold' },
  { key: 'building', label: 'Building', iconify: 'solar:buildings-bold' },
  { key: 'settings', label: 'Settings', iconify: 'solar:settings-bold' },
  { key: 'search', label: 'Search', iconify: 'solar:magnifer-bold' },
  { key: 'qr', label: 'QR Code', iconify: 'solar:qr-code-bold' },
  { key: 'scan', label: 'Scan', iconify: 'solar:scan-bold' },
  { key: 'box', label: 'Box (default)', iconify: 'solar:box-bold' },
];

export const DEFAULT_MODULE_ICON_KEY = 'box';
export const DEFAULT_MODULE_ICONIFY = 'solar:box-bold';

/**
 * Aliases for legacy/synonym values. Keys MUST be normalized (lowercase, kebab).
 * Values MUST exist in MODULE_ICON_OPTIONS.
 */
const ALIAS_TO_KEY: Record<string, string> = {
  tickets: 'ticket',
  'gift-card': 'gift',
  gift_card: 'gift',
  card: 'membership',
  event: 'calendar',
  events: 'calendar',
  store: 'shop',
  account: 'user',
  information: 'info',
};

function normalize(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .toLowerCase();
}

/**
 * Resolve any legacy or canonical icon value to a stable catalog key.
 * Falls back to the default key if no match is found.
 *
 * Accepts: full iconify ids (`solar:foo-bold` → returned unchanged for `resolveModuleIconify`,
 * but normalized to default key here since iconify ids are not catalog keys).
 */
export function resolveModuleIconKey(input: string | null | undefined): string {
  if (!input) return DEFAULT_MODULE_ICON_KEY;
  if (input.includes(':')) {
    // Full iconify id — try to find a catalog entry that uses it.
    const match = MODULE_ICON_OPTIONS.find((o) => o.iconify === input);
    return match?.key ?? DEFAULT_MODULE_ICON_KEY;
  }
  const norm = normalize(input);
  if (ALIAS_TO_KEY[norm]) return ALIAS_TO_KEY[norm];
  if (MODULE_ICON_OPTIONS.some((o) => o.key === norm)) return norm;
  return DEFAULT_MODULE_ICON_KEY;
}

/**
 * Resolve any module icon value to a renderable iconify identifier.
 *  - Full iconify id (`set:name`) → returned unchanged.
 *  - Catalog key (`ticket`) → mapped via catalog.
 *  - Alias (`tickets`, `gift_card`) → normalized via ALIAS_TO_KEY then catalog.
 *  - Unknown / null → DEFAULT_MODULE_ICONIFY.
 */
export function resolveModuleIconify(input: string | null | undefined): string {
  if (!input) return DEFAULT_MODULE_ICONIFY;
  if (input.includes(':')) return input;
  const key = resolveModuleIconKey(input);
  return MODULE_ICON_OPTIONS.find((o) => o.key === key)?.iconify ?? DEFAULT_MODULE_ICONIFY;
}
